import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import React, { useEffect, useState } from 'react'
import styled from 'styled-components'

import COLORS from 'pared/constants/colors'
import { MOBILE_WIDTH } from 'pared/constants/styles'

import { HIDE_SALES_FORECAST_ADJUSTMENT } from '../constants'

const ForecastContainer = styled.div`
  margin-bottom: 40px;
  width: 100%;
  min-width: 800px;
  max-width: 1200px;

  @media ${MOBILE_WIDTH} {
    width: 100vw;
    min-width: initial;
    padding: 5px;
    box-sizing: border-box;
  }
`

const ForecastCard = styled.div`
  background-color: #f8f8f8;
  border-radius: 8px;
  padding: 30px;
  width: 100%;
  box-sizing: border-box;

  @media ${MOBILE_WIDTH} {
    padding: 20px 15px;
  }
`

const Title = styled.h2`
  margin: 0 0 25px 0;
  font-family: Lexend-SemiBold;
  font-size: 24px;
  font-weight: 600;
  color: ${COLORS.Chalkboard};

  @media ${MOBILE_WIDTH} {
    font-size: 20px;
    margin-bottom: 20px;
  }
`

const ForecastRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }

  @media ${MOBILE_WIDTH} {
    margin-bottom: 15px;
  }
`

const Label = styled.span`
  font-size: 16px;
  color: ${COLORS.Chalkboard};
  font-weight: 500;

  @media ${MOBILE_WIDTH} {
    font-size: 14px;
  }
`

const Value = styled.span`
  font-family: Lexend-Regular;
  font-size: 28px;
  font-weight: 700;
  color: ${COLORS.Basil};

  @media ${MOBILE_WIDTH} {
    font-size: 24px;
  }
`

const AdjustedValue = styled(Value)`
  color: ${COLORS.Plum};
`

const AdjustmentColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`

const AdjustmentWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const AdjustmentHint = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: -8px 0 4px 0;
  font-size: 13px;
  line-height: 1.5;
  color: ${COLORS.Steel};

  svg {
    font-size: 1rem;
    flex-shrink: 0;
    margin-top: 2px;
  }

  @media ${MOBILE_WIDTH} {
    font-size: 12px;
  }
`

const AdjustmentInput = styled.input<{ backgroundColor?: string }>`
  width: 80px;
  height: 40px;
  padding: 8px;
  border: 1px solid #e0e0e0;
  border-radius: 4px;
  font-size: 16px;
  text-align: center;
  background-color: ${(props) => props.backgroundColor || 'white'};

  &:focus {
    outline: none;
    border-color: ${COLORS.Plum};
  }

  @media ${MOBILE_WIDTH} {
    width: 60px;
    font-size: 14px;
  }
`

const PercentLabel = styled.span`
  font-family: Lexend-Regular;
  font-size: 16px;
  color: ${COLORS.Steel};

  @media ${MOBILE_WIDTH} {
    font-size: 14px;
  }
`

const PrepDateDiv = styled.div`
  font-size: 16px;
  color: ${COLORS.Chalkboard};
  font-weight: 500;
  padding: 10px 30px;

  @media ${MOBILE_WIDTH} {
    font-size: 14px;
    padding: 10px 15px;
  }
`

interface IProps {
  date: string
  baseForecast: number
  adjustment: number
  globalDefault: number
  weeklySalesForecast: number
  onAdjustmentChange: (value: number) => void
  disableInput?: boolean
}

const SalesForecast: React.FC<IProps> = ({
  date,
  weeklySalesForecast,
  baseForecast,
  adjustment,
  globalDefault,
  onAdjustmentChange,
  disableInput,
}) => {
  const adjustedForecast = baseForecast * (1 + adjustment / 100)
  const [inputValue, setInputValue] = useState<string>(
    adjustment === 0 ? '' : String(adjustment),
  )

  // Sync local input value with external adjustment prop
  useEffect(() => {
    setInputValue(adjustment === 0 ? '' : String(adjustment))
  }, [adjustment])

  const handleAdjustmentChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value
    setInputValue(newValue)

    // Handle empty input
    if (newValue === '') {
      onAdjustmentChange(0)
      return
    }

    // Allow typing negative sign or decimal point
    if (newValue === '-' || newValue === '.' || newValue === '-.') {
      return
    }

    const numValue = parseFloat(newValue)
    // Only update if it's a valid number
    if (!isNaN(numValue)) {
      onAdjustmentChange(numValue)
    }
  }

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
  }

  return (
    <ForecastContainer>
      <Title>Sales Forecast</Title>
      <PrepDateDiv>Prep Date: {date}</PrepDateDiv>
      <ForecastCard>
        <ForecastRow>
          <Label>Weekly Forecast:</Label>
          <Value>{formatCurrency(weeklySalesForecast)}</Value>
        </ForecastRow>
        <ForecastRow>
          <Label>Base Forecast:</Label>
          <Value>{formatCurrency(baseForecast)}</Value>
        </ForecastRow>
        {!HIDE_SALES_FORECAST_ADJUSTMENT && (
          <>
            <ForecastRow>
              <Label>Adjustment:</Label>
              <AdjustmentColumn>
                <AdjustmentWrapper>
                  <AdjustmentInput
                    type="number"
                    inputMode="decimal"
                    value={inputValue}
                    onChange={handleAdjustmentChange}
                    placeholder="0"
                    step="1"
                    disabled={disableInput}
                    backgroundColor={disableInput ? '#f5f5f5' : 'white'}
                  />
                  <PercentLabel>%</PercentLabel>
                </AdjustmentWrapper>
              </AdjustmentColumn>
            </ForecastRow>
            {!disableInput && (
              <AdjustmentHint>
                <InfoOutlinedIcon />
                <span>
                  Company-wide adjustment: {globalDefault}%. Enter a different
                  percentage above to override the company adjustment at this
                  store.
                </span>
              </AdjustmentHint>
            )}
            <ForecastRow>
              <Label>Adjusted Forecast:</Label>
              <AdjustedValue>{formatCurrency(adjustedForecast)}</AdjustedValue>
            </ForecastRow>
          </>
        )}
      </ForecastCard>
    </ForecastContainer>
  )
}

export default SalesForecast
